import { ClbIcon } from '@celebration/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useNavigate } from 'react-router-dom';
import IPoolConfiguration from '../../../../common/interfaces/pool-configuration/IPoolConfiguration';
import AuthContext from '../../../../app/contexts/auth';
import IPoolTimes from '../../interfaces/IPoolTimes';
import Input from '../../../../common/components/input';
import UpdateConfirmationModal from '../update-confirmation-modal';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import IValidationResult from '../../../../common/interfaces/IValidationResult';
import WelcomeConstants from '../../../welcome/constants';
import { SULAHUB_ROLES } from '../../../../common/constants/roles';
import RoundManagerApiService from '../../../../common/services/roundmanager-api';
import { IPoolConfigurationErrorsSchema } from '../../interfaces/IPoolConfigurationErrorsSchema';
import './loads-pool.scss';

interface IPoolConfigData {
    finishedProductRoundsQuantityLimit: string;
    directSellingRoundsQuantityLimit: string;
    inputsRoundsQuantityLimit: string;
    finishedProductTimeLimit: string;
    directSellingTimeLimit: string;
    inputsTimeLimit: string;
}

function LoadsPool() {
    const LOADS_POOL_FIELDS = 6;

    const { t } = useTranslation();
    const { user, isAuthorized } = AuthContext.useAuth();
    const { setToast } = useToast();
    const navigate = useNavigate();

    const emptyPoolConfigData = useMemo<IPoolConfigData>(
        () => ({
            finishedProductRoundsQuantityLimit: '0',
            directSellingRoundsQuantityLimit: '0',
            inputsRoundsQuantityLimit: '0',
            finishedProductTimeLimit: '00:00',
            directSellingTimeLimit: '00:00',
            inputsTimeLimit: '00:00',
        }),
        [],
    );

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(() => {
        const isEditing: boolean[] = [];

        for (let i = 0; i < LOADS_POOL_FIELDS; i++) isEditing.push(false);

        return isEditing;
    });
    const allPoolConfigurations = useRef<IPoolConfiguration[]>([]);
    const [currentPoolConfiguration, setCurrentPoolConfiguration] = useState(
        {} as IPoolConfiguration,
    );
    const [displayedTimes, setDisplayedTimes] = useState({} as IPoolTimes);
    const [modalTitle, setModalTitle] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [poolConfigData, setPoolConfigData] = useState(emptyPoolConfigData);
    const [modalContent, setModalContent] = useState('');

    const validatePoolConfiguration = useCallback((): IValidationResult => {
        const result =
            RoundManagerApiService.Configurations.poolConfigurationSchema.safeParse(
                poolConfigData,
            );

        const validationResult = {
            success: result.success,
        } as IValidationResult;

        if (!result.success) {
            validationResult.errors = result.error.format();
        }

        return validationResult;
    }, [poolConfigData]);

    const getCurrentPoolConfiguration = useCallback((): IPoolConfiguration => {
        return allPoolConfigurations.current.find(
            r => r.country === user?.country,
        )!;
    }, [user]);

    const closeModal = useCallback(() => {
        const currentPoolConfiguration = getCurrentPoolConfiguration();

        setPoolConfigData({
            finishedProductRoundsQuantityLimit:
                currentPoolConfiguration.roundLimits.find(
                    r => r.channel == 'Produto Acabado',
                )?.roundQuantityLimit!,
            directSellingRoundsQuantityLimit:
                currentPoolConfiguration.roundLimits.find(
                    r => r.channel == 'Venda Direta',
                )?.roundQuantityLimit!,
            inputsRoundsQuantityLimit:
                currentPoolConfiguration.roundLimits.find(
                    r => r.channel == 'Insumos',
                )?.roundQuantityLimit!,
            finishedProductTimeLimit: currentPoolConfiguration.timeLimits.find(
                r => r.channel == 'Produto Acabado',
            )?.timeLimit!,
            directSellingTimeLimit: currentPoolConfiguration.timeLimits.find(
                r => r.channel == 'Venda Direta',
            )?.timeLimit!,
            inputsTimeLimit: currentPoolConfiguration.timeLimits.find(
                r => r.channel == 'Insumos',
            )?.timeLimit!,
        });
        setIsModalOpen(false);
    }, [getCurrentPoolConfiguration]);

    const processValidationErrors = useCallback(
        (validationResult: IValidationResult) => {
            const errors =
                validationResult.errors as IPoolConfigurationErrorsSchema;

            Object.keys(errors).forEach(k => {
                if (k !== '_errors')
                    (errors as any)[k]._errors.forEach((err: any) =>
                        setToast('negative', t(err)),
                    );
            });
        },
        [setToast, t],
    );

    const updatePoolConfiguration = useCallback(async () => {
        const validationResult = validatePoolConfiguration();

        if (!validationResult?.success) {
            processValidationErrors(validationResult);

            const currentPoolConfiguration = getCurrentPoolConfiguration();
            setPoolConfigData(() => ({
                finishedProductRoundsQuantityLimit:
                    currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Produto Acabado',
                    )?.roundQuantityLimit!,
                directSellingRoundsQuantityLimit:
                    currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Venda Direta',
                    )?.roundQuantityLimit!,
                inputsRoundsQuantityLimit:
                    currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Insumos',
                    )?.roundQuantityLimit!,
                finishedProductTimeLimit:
                    currentPoolConfiguration.timeLimits.find(
                        t => t.channel == 'Produto Acabado',
                    )?.timeLimit!,
                directSellingTimeLimit:
                    currentPoolConfiguration.timeLimits.find(
                        t => t.channel == 'Venda Direta',
                    )?.timeLimit!,
                inputsTimeLimit: currentPoolConfiguration.timeLimits.find(
                    t => t.channel == 'Insumos',
                )?.timeLimit!,
            }));
            setIsModalOpen(false);
            return;
        }

        const configIdx = allPoolConfigurations.current.findIndex(
            r => r.id === currentPoolConfiguration.id,
        );

        const currentConfig = allPoolConfigurations.current[configIdx];
        const updatedConfig: IPoolConfiguration = {
            ...currentPoolConfiguration,
            roundLimits: [
                {
                    ...currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Produto Acabado',
                    )!,
                    roundQuantityLimit:
                        poolConfigData.finishedProductRoundsQuantityLimit,
                },
                {
                    ...currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Venda Direta',
                    )!,
                    roundQuantityLimit:
                        poolConfigData.directSellingRoundsQuantityLimit,
                },
                {
                    ...currentPoolConfiguration.roundLimits.find(
                        r => r.channel == 'Insumos',
                    )!,
                    roundQuantityLimit:
                        poolConfigData.inputsRoundsQuantityLimit,
                },
            ],
            timeLimits: [
                {
                    ...currentPoolConfiguration.timeLimits.find(
                        t => t.channel == 'Produto Acabado',
                    )!,
                    timeLimit: poolConfigData.finishedProductTimeLimit,
                },
                {
                    ...currentPoolConfiguration.timeLimits.find(
                        t => t.channel == 'Venda Direta',
                    )!,
                    timeLimit: poolConfigData.directSellingTimeLimit,
                },
                {
                    ...currentPoolConfiguration.timeLimits.find(
                        t => t.channel == 'Insumos',
                    )!,
                    timeLimit: poolConfigData.inputsTimeLimit,
                },
            ],
        };

        if (JSON.stringify(updatedConfig) !== JSON.stringify(currentConfig)) {
            const { response, error } =
                await RoundManagerApiService.Configurations.updatePoolConfiguration(
                    updatedConfig,
                );

            if (response) {
                setToast('positive', t('PoolConfigurationUpdated'));

                allPoolConfigurations.current[configIdx] = updatedConfig;
                setCurrentPoolConfiguration({ ...updatedConfig });
            } else {
                setToast('negative', t('Erro'), error?.message);
                setPoolConfigData(() => ({
                    finishedProductRoundsQuantityLimit:
                        currentPoolConfiguration.roundLimits.find(
                            c => c.channel == 'Produto Acabado',
                        )?.roundQuantityLimit!,
                    directSellingRoundsQuantityLimit:
                        currentPoolConfiguration.roundLimits.find(
                            c => c.channel == 'Venda Direta',
                        )?.roundQuantityLimit!,
                    inputsRoundsQuantityLimit:
                        currentPoolConfiguration.roundLimits.find(
                            c => c.channel == 'Insumos',
                        )?.roundQuantityLimit!,
                    finishedProductTimeLimit:
                        currentPoolConfiguration.timeLimits.find(
                            c => c.channel == 'Produto Acabado',
                        )?.timeLimit!,
                    directSellingTimeLimit:
                        currentPoolConfiguration.timeLimits.find(
                            c => c.channel == 'Venda Direta',
                        )?.timeLimit!,
                    inputsTimeLimit: currentPoolConfiguration.timeLimits.find(
                        c => c.channel == 'Insumos',
                    )?.timeLimit!,
                }));
            }
        }

        setIsModalOpen(false);
    }, [
        t,
        setToast,
        currentPoolConfiguration,
        getCurrentPoolConfiguration,
        validatePoolConfiguration,
        processValidationErrors,
        poolConfigData,
    ]);

    const formatPoolTimeLimits = useCallback(
        (phaseDuration: string): string => {
            const hours = phaseDuration.substring(0, 2);
            const minutes = phaseDuration.substring(3, 5);

            const formatedPhaseDuration = '';

            return `${
                formatedPhaseDuration
            }${hours}:${minutes} ${t('FormatedHourDuration')}`;
        },
        [t],
    );

    const loadCountryPoolConfigurations = useCallback(async () => {
        if (allPoolConfigurations.current.length === 0) {
            const { response, error } =
                await RoundManagerApiService.Configurations.getAllPoolConfigurations();

            if (error) {
                setToast('negative', t('Erro'), error?.message);
                navigate(WelcomeConstants.default);

                return;
            }

            if (response) {
                allPoolConfigurations.current = response;
            }
        }

        const currentPoolConfig = getCurrentPoolConfiguration();

        setCurrentPoolConfiguration(currentPoolConfig);
        setPoolConfigData({
            finishedProductRoundsQuantityLimit:
                currentPoolConfig.roundLimits.find(
                    p => p.channel == 'Produto Acabado',
                )?.roundQuantityLimit!,
            directSellingRoundsQuantityLimit:
                currentPoolConfig.roundLimits.find(
                    p => p.channel == 'Venda Direta',
                )?.roundQuantityLimit!,
            inputsRoundsQuantityLimit: currentPoolConfig.roundLimits.find(
                p => p.channel == 'Insumos',
            )?.roundQuantityLimit!,
            finishedProductTimeLimit: currentPoolConfig.timeLimits.find(
                p => p.channel == 'Produto Acabado',
            )?.timeLimit!,
            directSellingTimeLimit: currentPoolConfig.timeLimits.find(
                p => p.channel == 'Venda Direta',
            )?.timeLimit!,
            inputsTimeLimit: currentPoolConfig.timeLimits.find(
                p => p.channel == 'Insumos',
            )?.timeLimit!,
        });
        setIsLoading(false);
    }, [t, setToast, getCurrentPoolConfiguration, navigate]);

    useEffect(() => {
        loadCountryPoolConfigurations();
    }, [loadCountryPoolConfigurations]);

    useEffect(() => {
        const displayedTimes: IPoolTimes = {
            displayedFinishedProductTimeLimit: formatPoolTimeLimits(
                poolConfigData.finishedProductTimeLimit,
            ),
            displayedDirectSellingTimeLimit: formatPoolTimeLimits(
                poolConfigData.directSellingTimeLimit,
            ),
            displayedInputsTimeLimit: formatPoolTimeLimits(
                poolConfigData.inputsTimeLimit,
            ),
        };

        setDisplayedTimes(displayedTimes);
    }, [poolConfigData, formatPoolTimeLimits]);

    const modalContents = useMemo(
        () => [
            'UpdateNumberOfRoundsToMoveLoadToPoolContent',
            'UpdateScheduledTimeIncludeLoadsToPoolContent',
        ],
        [],
    );

    const roundPoolOptions = useMemo(
        () => [
            {
                id: 0,
                key: t('Produto Acabado'),
                value: `${currentPoolConfiguration?.roundLimits?.find(r => r.channel == 'Produto Acabado')?.roundQuantityLimit!} ${t('Rounds')}`, // `${poolConfigData.finishedProductRoundsQuantityLimit} ${t('Rounds')}`,
                action: () => {
                    setModalTitle(t('UpdateNumberOfRoundsToMoveLoadToPool'));
                    setModalContent(t(modalContents[0]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 1,
                key: t('Venda Direta'),
                value: `${currentPoolConfiguration?.roundLimits?.find(r => r.channel == 'Venda Direta')?.roundQuantityLimit!} ${t('Rounds')}`, // `${poolConfigData.directSellingRoundsQuantityLimit} ${t('Rounds')}`,
                action: () => {
                    setModalTitle(t('UpdateNumberOfRoundsToMoveLoadToPool'));
                    setModalContent(t(modalContents[0]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 2,
                key: t('Insumos'),
                value: `${currentPoolConfiguration?.roundLimits?.find(r => r.channel == 'Insumos')?.roundQuantityLimit!} ${t('Rounds')}`, // `${poolConfigData.inputsRoundsQuantityLimit} ${t('Rounds')}`,
                action: () => {
                    setModalTitle(t('UpdateNumberOfRoundsToMoveLoadToPool'));
                    setModalContent(t(modalContents[0]));
                    setIsModalOpen(true);
                },
            },
        ],
        [t, poolConfigData, modalContents],
    );

    const scheduledPoolOptions = useMemo(
        () => [
            {
                id: 3,
                key: t('Produto Acabado'),
                value: `${displayedTimes.displayedFinishedProductTimeLimit}`,
                action: () => {
                    setModalTitle(t('UpdateScheduledTimeIncludeLoadsToPool'));
                    setModalContent(t(modalContents[1]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 4,
                key: t('Venda Direta'),
                value: `${displayedTimes.displayedDirectSellingTimeLimit}`,
                action: () => {
                    setModalTitle(t('UpdateScheduledTimeIncludeLoadsToPool'));
                    setModalContent(t(modalContents[1]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 5,
                key: t('Insumos'),
                value: `${displayedTimes.displayedInputsTimeLimit}`,
                action: () => {
                    setModalTitle(t('UpdateScheduledTimeIncludeLoadsToPool'));
                    setModalContent(t(modalContents[1]));
                    setIsModalOpen(true);
                },
            },
        ],
        [t, displayedTimes, modalContents],
    );

    return (
        <>
            <div className="preferences-inside-container">
                <div className="container-title">
                    <h1>{t('LoadsPoolManagement')}</h1>
                </div>

                {isLoading && (
                    <div className="loading-container">
                        <ClbLoading />
                    </div>
                )}

                {!isLoading && (
                    <div className="container-options">
                        <span>
                            <b>{t('NumberRoundsMoveLoadToPool')}</b>
                        </span>
                        {roundPoolOptions.map(roundOption => (
                            <div
                                className={`option option-pool${
                                    !isAuthorized(
                                        SULAHUB_ROLES.SYSTEM_PROFILES
                                            .OPERATION_USER,
                                    )
                                        ? ''
                                        : ' no-edit'
                                }`}
                                key={roundOption.id}
                            >
                                {!isEditing[roundOption.id] && (
                                    <div>
                                        <span>{roundOption.key}</span>
                                        <span>{roundOption.value}</span>
                                    </div>
                                )}

                                {roundOption.id === 0 &&
                                    isEditing[roundOption.id] && (
                                        <Input
                                            label={t(roundOption.key)}
                                            value={
                                                poolConfigData.finishedProductRoundsQuantityLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    finishedProductRoundsQuantityLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            dataTestId="pool-round-limit-finishedProduct"
                                        />
                                    )}

                                {roundOption.id === 1 &&
                                    isEditing[roundOption.id] && (
                                        <Input
                                            label={t(roundOption.key)}
                                            value={
                                                poolConfigData.directSellingRoundsQuantityLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    directSellingRoundsQuantityLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            dataTestId="pool-round-limit-directSelling"
                                        />
                                    )}

                                {roundOption.id === 2 &&
                                    isEditing[roundOption.id] && (
                                        <Input
                                            label={t(roundOption.key)}
                                            value={
                                                poolConfigData.inputsRoundsQuantityLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    inputsRoundsQuantityLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            dataTestId="pool-round-limit-inputs"
                                        />
                                    )}

                                {!isAuthorized(
                                    SULAHUB_ROLES.SYSTEM_PROFILES
                                        .OPERATION_USER,
                                ) && (
                                    <button
                                        type="button"
                                        disabled={
                                            !isEditing[roundOption.id] &&
                                            isEditing.filter(x => x).length !==
                                                0
                                        }
                                        onClick={() => {
                                            const editingField =
                                                isEditing[roundOption.id];

                                            if (editingField)
                                                roundOption.action();

                                            setIsEditing(s => {
                                                s[roundOption.id] =
                                                    !editingField;
                                                return [...s];
                                            });
                                        }}
                                        data-testid={`${roundOption.id}-${isEditing[roundOption.id] ? 'confirm' : 'edit'}-button`}
                                    >
                                        <ClbIcon
                                            icon={
                                                isEditing[roundOption.id]
                                                    ? 'check'
                                                    : 'edit'
                                            }
                                            size="md"
                                        />
                                    </button>
                                )}
                            </div>
                        ))}

                        <br />
                        <br />

                        <span>
                            <b>{t('ScheduledTimeIncludeLoadsToPool')}</b>
                        </span>

                        {scheduledPoolOptions.map(scheduledOption => (
                            <div
                                className={`option option-pool${
                                    !isAuthorized(
                                        SULAHUB_ROLES.SYSTEM_PROFILES
                                            .OPERATION_USER,
                                    )
                                        ? ''
                                        : ' no-edit'
                                }`}
                                key={scheduledOption.id}
                            >
                                {!isEditing[scheduledOption.id] && (
                                    <div>
                                        <span>{scheduledOption.key}</span>
                                        <span>{scheduledOption.value}</span>
                                    </div>
                                )}

                                {scheduledOption.id === 3 &&
                                    isEditing[scheduledOption.id] && (
                                        <Input
                                            label={t(scheduledOption.key)}
                                            value={
                                                poolConfigData.finishedProductTimeLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    finishedProductTimeLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            icon="time"
                                            dataTestId="pool-time-limit-finishedProduct"
                                        />
                                    )}

                                {scheduledOption.id === 4 &&
                                    isEditing[scheduledOption.id] && (
                                        <Input
                                            label={t(scheduledOption.key)}
                                            value={
                                                poolConfigData.directSellingTimeLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    directSellingTimeLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            icon="time"
                                            dataTestId="pool-time-limit-directSelling"
                                        />
                                    )}

                                {scheduledOption.id === 5 &&
                                    isEditing[scheduledOption.id] && (
                                        <Input
                                            label={t(scheduledOption.key)}
                                            value={
                                                poolConfigData.inputsTimeLimit
                                            }
                                            onChange={e =>
                                                setPoolConfigData(s => ({
                                                    ...s,
                                                    inputsTimeLimit:
                                                        e.target.value,
                                                }))
                                            }
                                            icon="time"
                                            dataTestId="pool-time-limit-inputs"
                                        />
                                    )}

                                {!isAuthorized(
                                    SULAHUB_ROLES.SYSTEM_PROFILES
                                        .OPERATION_USER,
                                ) && (
                                    <button
                                        type="button"
                                        disabled={
                                            !isEditing[scheduledOption.id] &&
                                            isEditing.filter(x => x).length !==
                                                0
                                        }
                                        onClick={() => {
                                            const editingField =
                                                isEditing[scheduledOption.id];

                                            if (editingField)
                                                scheduledOption.action();

                                            setIsEditing(s => {
                                                s[scheduledOption.id] =
                                                    !editingField;
                                                return [...s];
                                            });
                                        }}
                                        data-testid={`${scheduledOption.id}-${isEditing[scheduledOption.id] ? 'confirm' : 'edit'}-button`}
                                    >
                                        <ClbIcon
                                            icon={
                                                isEditing[scheduledOption.id]
                                                    ? 'check'
                                                    : 'edit'
                                            }
                                            size="md"
                                        />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <br />

                {!isLoading && (
                    <div className="fixed-rules">
                        <b>{t('FixedRulesTitle')}</b> <br />
                        {t('PoolFixedRuleLoadingDate')} <br />
                        {t('PoolFixedRulePoolAlwaysPool')} <br />
                        {t('PoolFixedRuleInvisibleTime')}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <UpdateConfirmationModal
                    title={modalTitle}
                    content={modalContent}
                    opened={isModalOpen}
                    closeModal={closeModal}
                    closeModalCallback={updatePoolConfiguration}
                />
            )}
        </>
    );
}

export default LoadsPool;
